import { formatDistanceToNow } from 'date-fns';
import type { CSSProperties } from 'react';

import { BarList } from '@/components/common/bar-list';
import { PageHeader } from '@/components/common/page-header';
import { QueryDegradedNotice } from '@/components/common/query-degraded-notice';
import { SectionCard } from '@/components/common/section-card';
import { Sparkline, useSparklineBuffer } from '@/components/common/sparkline';
import { StatTile } from '@/components/common/stat-tile';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAnalyticsOverview } from '@/hooks/use-analytics-overview';
import { useConnectedUsers } from '@/hooks/use-connected-users';
import { usePopularRegions } from '@/hooks/use-popular-regions';
import { useUserGeography } from '@/hooks/use-user-geography';
import { countryFlag } from '@/lib/country-flag';

const NUMBER = new Intl.NumberFormat('en-US');

// Sparkline/accent strokes drawn from the operator status spectrum.
const ACCENT_INFLIGHT = 'hsl(205 92% 60%)';
const ACCENT_ACTIVE = 'hsl(150 64% 50%)';
const ACCENT_DEGRADED = 'hsl(38 95% 58%)';

function KpiRow(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useAnalyticsOverview();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-[92px] w-full" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <ErrorState
        title="Couldn't load the overview."
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatTile
        label="Connected users"
        value={NUMBER.format(data.connected_users)}
        sub={`${NUMBER.format(data.total_users)} total`}
        accent={ACCENT_INFLIGHT}
      />
      <StatTile
        label="Total users"
        value={NUMBER.format(data.total_users)}
        sub="registered accounts"
        accent={ACCENT_INFLIGHT}
      />
      <StatTile
        label="Active nodes"
        value={`${data.active_nodes}/${data.total_nodes}`}
        sub="reporting healthy"
        accent={ACCENT_ACTIVE}
      />
      <StatTile
        label="Regions covered"
        value={String(data.regions_covered)}
        sub="countries with exits"
        accent={ACCENT_DEGRADED}
      />
    </div>
  );
}

function ConnectedUsersPanel(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useConnectedUsers();
  // When the API omits a time series, accumulate the polled total client-side.
  const buffer = useSparklineBuffer(data?.series ? undefined : data?.total);
  const points = data?.series ?? buffer;

  return (
    <SectionCard title="Connected users" className="lg:col-span-3">
      {isLoading && <TableSkeleton rows={3} />}

      {isError && (
        <ErrorState
          title="Couldn't load connected users."
          error={error}
          onRetry={() => void refetch()}
        />
      )}

      {data && (
        <div className="space-y-3">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="font-mono text-3xl font-semibold tracking-tight">
                {NUMBER.format(data.total)}
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                {NUMBER.format(data.breakdown.vpn_sessions)} VPN ·{' '}
                {NUMBER.format(data.breakdown.proxy_sessions)} proxy
              </p>
            </div>
            <Sparkline points={points} color={ACCENT_INFLIGHT} />
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function TopRegionsPanel(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = usePopularRegions();

  return (
    <SectionCard title="Top regions" className="lg:col-span-2">
      {isLoading && <TableSkeleton rows={4} />}

      {isError && (
        <ErrorState
          title="Couldn't load regions."
          error={error}
          onRetry={() => void refetch()}
        />
      )}

      {data?.degraded && <QueryDegradedNotice />}

      {data && !data.degraded && (
        <BarList
          items={data.regions.map((r) => ({
            label: r.country_code
              ? `${countryFlag(r.country_code)} ${r.country_code}`.trim()
              : 'Unknown',
            hint: r.continent ?? undefined,
            value: r.active_sessions,
            href: '/insights/regions',
          }))}
          valueFormat={(v) => NUMBER.format(v)}
          emptyMessage="No active sessions yet."
        />
      )}
    </SectionCard>
  );
}

function UserBasePanel(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useUserGeography();

  return (
    <SectionCard
      title="User base by country"
      description="Registered accounts, by resolved country."
    >
      {isLoading && <TableSkeleton rows={6} />}

      {isError && (
        <ErrorState
          title="Couldn't load user geography."
          error={error}
          onRetry={() => void refetch()}
        />
      )}

      {data && data.countries.length === 0 && (
        <EmptyState message="No users yet" />
      )}

      {data && data.countries.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Country</TableHead>
              <TableHead className="text-right">Users</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.countries.map((c) => (
              <TableRow key={c.country_code}>
                <TableCell>
                  <span className="mr-1.5" aria-hidden>
                    {countryFlag(c.country_code)}
                  </span>
                  {c.country_name}
                </TableCell>
                <TableCell className="text-right font-mono">{NUMBER.format(c.users)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
}

function LastUpdated(): JSX.Element | null {
  const { dataUpdatedAt } = useAnalyticsOverview();
  if (!dataUpdatedAt) return null;
  return (
    <span className="font-mono text-xs text-muted-foreground">
      {formatDistanceToNow(dataUpdatedAt, { addSuffix: true })}
    </span>
  );
}

/** Shared live-pulse indicator used in page headers. */
function LiveIndicator(): JSX.Element {
  return (
    <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
      <span
        className="signal-dot animate-pulse-dot bg-status-active"
        style={{ ['--bg-dot']: 'var(--status-active)' } as CSSProperties}
        aria-hidden
      />
      Live
    </span>
  );
}

export function OverviewPage(): JSX.Element {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Overview"
        description="Live platform health — connected users, fleet capacity, reach."
        actions={
          <>
            <LiveIndicator />
            <LastUpdated />
          </>
        }
      />

      <KpiRow />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <ConnectedUsersPanel />
        <TopRegionsPanel />
      </div>

      <UserBasePanel />
    </div>
  );
}
